import React from "react";
import { Box, Modal } from "@mui/material";
import PropTypes from "prop-types";

/**
 * GMA-1869: a contact shared onto clients belonging to different admin logins
 * could not be removed from all of them, and this modal gave no warning — it
 * listed every linked client, offered "Remove from all", then failed with a
 * bare error. Removing a contact from a client requires managing that client,
 * so any linked client missing from the caller's own roster (`myClientIds`)
 * makes remove-from-all impossible. Say so up front and point at the way
 * forward instead of letting the user discover it by failing.
 *
 * `myClientIds` undefined means the roster has not loaded — stay permissive
 * and let the server decide rather than blocking on a guess.
 */
const SharedContactDeleteModal = ({
  open,
  contactName,
  clientName,
  sharedClientNames = [],
  myClientIds,
  onClose,
  onRemoveFromClient,
  onRemoveFromAll,
}) => {
  const rosterKnown = Array.isArray(myClientIds);
  const manages = (c) =>
    !rosterKnown || myClientIds.some((id) => String(id) === String(c.id));
  const blockedClients = sharedClientNames.filter((c) => !manages(c));
  const hasBlocked = blockedClients.length > 0;
  const blockedNames = blockedClients.map((c) => c.name).join(", ");
  const isAre = blockedClients.length === 1 ? "is" : "are";

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="shared-contact-delete-modal"
      className="modal-cancel-notif"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 520,
          bgcolor: "background.paper",
          boxShadow: 24,
          backgroundColor: "#d0d0d0",
          border: "5px solid #000",
          p: 3,
          color: "#000000",
        }}
      >
        <div className="custom-modal-content">
          <h5>This contact is shared with multiple clients</h5>
          <p>
            <strong>{contactName}</strong> is currently linked to:{" "}
            <strong>
              {sharedClientNames
                .map((c) => (manages(c) ? c.name : `${c.name} (not one of your clients)`))
                .join(", ")}
            </strong>
          </p>
          {hasBlocked ? (
            <p style={{ color: "#8a1c1c" }}>
              You can only remove this contact from clients you manage, so
              removing from all clients is not available —{" "}
              <strong>{blockedNames}</strong> {isAre} outside your access. Ask an
              admin who manages {blockedClients.length === 1 ? "it" : "them"} to
              remove it, or remove it from your own clients one at a time.
            </p>
          ) : (
            <p>
              Would you like to remove them from just{" "}
              <strong>{clientName}</strong>, or from all clients?
            </p>
          )}
        </div>
        <div
          className="modal-footer-btns"
          style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "16px" }}
        >
          <button
            type="button"
            onClick={onRemoveFromClient}
            style={{
              padding: "10px 16px",
              backgroundColor: "#1976d2",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Remove from {clientName} only
          </button>
          <button
            type="button"
            onClick={onRemoveFromAll}
            disabled={hasBlocked}
            title={
              hasBlocked
                ? `Not available — ${blockedNames} ${isAre} outside your access`
                : undefined
            }
            style={{
              padding: "10px 16px",
              backgroundColor: hasBlocked ? "#9e9e9e" : "#c62828",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: hasBlocked ? "not-allowed" : "pointer",
              fontWeight: "bold",
            }}
          >
            Remove from all clients ({sharedClientNames.length})
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "10px 16px",
              backgroundColor: "#666",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Go Back
          </button>
        </div>
      </Box>
    </Modal>
  );
};

SharedContactDeleteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  contactName: PropTypes.string.isRequired,
  clientName: PropTypes.string.isRequired,
  sharedClientNames: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  ),
  /** Client ids the signed-in user may act on; undefined = roster not loaded. */
  myClientIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ),
  onClose: PropTypes.func.isRequired,
  onRemoveFromClient: PropTypes.func.isRequired,
  onRemoveFromAll: PropTypes.func.isRequired,
};

export default SharedContactDeleteModal;
